import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal, Input, BusinessHoursPicker, Select, Textarea } from '@components';
import { CreateRouteData, TransferPolicy } from '../types';
import { ProcessedErrors } from '@utils/errorHandler';

interface CreateRouteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateRouteData) => Promise<ProcessedErrors | undefined>;
}

interface RouteFormState {
  name: string;
  phone_number: string;
  business_hours: string;
  transfer_policy: TransferPolicy;
  transfer_required_variables: string; // comma-separated in the UI
  transfer_conditions: string;
  notification_email: string;
  return_call_on_no_answer: boolean;
}

const EMPTY_FORM: RouteFormState = {
  name: '',
  phone_number: '',
  business_hours: '',
  transfer_policy: 'immediate',
  transfer_required_variables: '',
  transfer_conditions: '',
  notification_email: '',
  return_call_on_no_answer: false,
};

const parseVariables = (value: string): string[] =>
  value
    .split(',')
    .map(v => v.trim())
    .filter(Boolean);

export const CreateRouteModal: React.FC<CreateRouteModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<RouteFormState>(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [backendErrors, setBackendErrors] = useState<ProcessedErrors | null>(null);

  const handleInputChange = (
    field: Exclude<keyof RouteFormState, 'return_call_on_no_answer'>,
    value: string
  ) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }

    if (backendErrors?.fieldErrors[field]) {
      setBackendErrors(prev => {
        if (!prev) return null;
        const newFieldErrors = { ...prev.fieldErrors };
        delete newFieldErrors[field];
        return { ...prev, fieldErrors: newFieldErrors };
      });
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setBackendErrors(null);
    }
  }, [isOpen]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('callRouting.nameRequiredError');
    }

    if (!formData.phone_number.trim()) {
      newErrors.phone_number = t('callRouting.phoneNumberRequiredError');
    }

    if (!formData.business_hours.trim()) {
      newErrors.business_hours = t('callRouting.businessHoursRequiredError');
    }

    if (
      formData.transfer_policy === 'required_info' &&
      parseVariables(formData.transfer_required_variables).length === 0
    ) {
      newErrors.transfer_required_variables = t('callRouting.transferRequiredVariablesError');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setBackendErrors(null);

    try {
      const backendErrs = await onSubmit({
        name: formData.name,
        phone_number: formData.phone_number,
        business_hours: formData.business_hours,
        transfer_policy: formData.transfer_policy,
        transfer_required_variables: parseVariables(formData.transfer_required_variables),
        transfer_conditions: formData.transfer_conditions.trim(),
        notification_email: formData.notification_email.trim(),
        return_call_on_no_answer: formData.return_call_on_no_answer,
      });

      if (backendErrs && Object.keys(backendErrs.fieldErrors).length > 0) {
        setBackendErrors(backendErrs);
        setLoading(false);
        return;
      }

      setFormData(EMPTY_FORM);
      setBackendErrors(null);
      onClose();
    } catch (error) {
      // Errors are handled by parent component via onSubmit return value
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData(EMPTY_FORM);
    setErrors({});
    setBackendErrors(null);
    onClose();
  };

  const getFieldError = (field: keyof RouteFormState): string | undefined => {
    return backendErrors?.fieldErrors[field] || errors[field];
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={t('callRouting.addCallRoute')} size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label
            htmlFor="route-name"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
          >
            {t('callRouting.nameRequired')}
          </label>
          <Input
            id="route-name"
            type="text"
            value={formData.name}
            onChange={e => handleInputChange('name', e.target.value)}
            placeholder={t('callRouting.enterRouteName')}
            errorText={getFieldError('name')}
            state={getFieldError('name') ? 'error' : 'default'}
            disabled={loading}
          />
        </div>

        <div>
          <label
            htmlFor="phone-number"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
          >
            {t('callRouting.phoneNumberRequired')}
          </label>
          <Input
            id="phone-number"
            type="tel"
            value={formData.phone_number}
            onChange={e => handleInputChange('phone_number', e.target.value)}
            placeholder={t('callRouting.phoneNumberPlaceholder')}
            errorText={getFieldError('phone_number')}
            state={getFieldError('phone_number') ? 'error' : 'default'}
            disabled={loading}
          />
        </div>

        <div>
          <label
            htmlFor="business-hours"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
          >
            {t('callRouting.businessHoursRequired')}
          </label>
          <BusinessHoursPicker
            value={formData.business_hours}
            onChange={value => handleInputChange('business_hours', value)}
            error={getFieldError('business_hours')}
            disabled={loading}
          />
        </div>

        <div>
          <label
            htmlFor="transfer-policy"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
          >
            {t('callRouting.transferPolicyLabel')}
          </label>
          <Select
            id="transfer-policy"
            options={[
              { value: 'immediate', label: t('callRouting.transferPolicyImmediate') },
              { value: 'required_info', label: t('callRouting.transferPolicyRequiredInfo') },
            ]}
            value={formData.transfer_policy}
            onChange={value => handleInputChange('transfer_policy', value as string)}
            disabled={loading}
          />
        </div>

        {formData.transfer_policy === 'required_info' && (
          <div>
            <label
              htmlFor="transfer-required-variables"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              {t('callRouting.transferRequiredVariablesLabel')}
            </label>
            <Input
              id="transfer-required-variables"
              type="text"
              value={formData.transfer_required_variables}
              onChange={e => handleInputChange('transfer_required_variables', e.target.value)}
              placeholder={t('callRouting.transferRequiredVariablesPlaceholder')}
              errorText={getFieldError('transfer_required_variables')}
              state={getFieldError('transfer_required_variables') ? 'error' : 'default'}
              disabled={loading}
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {t('callRouting.transferRequiredVariablesHelp')}
            </p>
          </div>
        )}

        <div>
          <label
            htmlFor="transfer-conditions"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
          >
            {t('callRouting.transferConditionsLabel')}
          </label>
          <Textarea
            id="transfer-conditions"
            value={formData.transfer_conditions}
            onChange={e => handleInputChange('transfer_conditions', e.target.value)}
            placeholder={t('callRouting.transferConditionsPlaceholder')}
            maxLength={1000}
            minRows={2}
            fullWidth
            disabled={loading}
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {t('callRouting.transferRulesDisclaimer')}
          </p>
        </div>

        <div>
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('callRouting.returnCallOnNoAnswerLabel')}
            </span>
            <label
              htmlFor="return-call-on-no-answer"
              className="relative inline-flex items-center cursor-pointer ml-3 shrink-0"
            >
              <span className="sr-only">{t('callRouting.returnCallOnNoAnswerLabel')}</span>
              <input
                id="return-call-on-no-answer"
                type="checkbox"
                className="sr-only peer"
                checked={formData.return_call_on_no_answer}
                onChange={e =>
                  setFormData(prev => ({ ...prev, return_call_on_no_answer: e.target.checked }))
                }
                disabled={loading}
              />
              <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-checked:bg-primary-600 rounded-full peer transition-colors peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600"></div>
            </label>
          </div>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {t('callRouting.returnCallOnNoAnswerHelp')}
          </p>
        </div>

        <div>
          <label
            htmlFor="notification-email"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
          >
            {t('callRouting.notificationEmailLabel')}
          </label>
          <Input
            id="notification-email"
            type="email"
            value={formData.notification_email}
            onChange={e => handleInputChange('notification_email', e.target.value)}
            placeholder={t('callRouting.notificationEmailPlaceholder')}
            errorText={getFieldError('notification_email')}
            state={getFieldError('notification_email') ? 'error' : 'default'}
            disabled={loading}
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {t('callRouting.notificationEmailHelp')}
          </p>
        </div>

        <div className="flex justify-end space-x-3 pt-4">
          <Button type="button" variant="outline" onClick={handleClose} disabled={loading}>
            {t('callRouting.cancel')}
          </Button>
          <Button type="submit" variant="gradient-multi" disabled={loading}>
            {loading ? t('callRouting.creating') : t('callRouting.createRoute')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
